import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApiService } from './base-api.service';
import type { ExportErrorsResponseApiDto, AutomationErrorsResponseApiDto } from 'src/app/shared/dto/export-errors-response.interface.dto';
import type { GrowthSignalsReportDetailsApiDto } from 'src/app/shared/dto/growth-signals-report-details.dto';

@Injectable({
  providedIn: 'root',
})
export class SignalsApiService {
  private readonly url = 'service/contactcloud/growth-signals-settings';
  private readonly errorsUrl = 'service/contactcloud/signals/export/errors';
  private readonly manualRunUrl = 'service/contactcloud/growth-signals/manual-run';
  private readonly reportDetailsUrl = 'service/contactcloud/growth-signals/report';

  constructor(private readonly baseApiService: BaseApiService) {}

  getSignals(headers: { clientId: string; clientAccountId: string }): Observable<any[]> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.get<any[]>(this.url, { showError: true, additionalHeaders });
  }

  createSignal(payload: Record<string, unknown>, headers: { clientId: string; clientAccountId: string }): Observable<any> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.post<any>(this.url, payload, { showError: true, additionalHeaders });
  }

  updateSignal(signalId: number | string, payload: Record<string, unknown>, headers: { clientId: string; clientAccountId: string }): Observable<any> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.patch<any>(`${this.url}/${signalId}`, payload, { showError: true, additionalHeaders });
  }

  deleteSignal(signalId: number | string, headers: { clientId: string; clientAccountId: string }): Observable<any> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.delete<any>(`${this.url}/${signalId}`, { showError: true, additionalHeaders });
  }

  getExportErrors(headers: { clientId: string; clientAccountId: string }): Observable<ExportErrorsResponseApiDto | AutomationErrorsResponseApiDto[]> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.get<ExportErrorsResponseApiDto | AutomationErrorsResponseApiDto[]>(
      this.errorsUrl,
      { showError: true, additionalHeaders },
    );
  }

  manualRunSignals(
    payload: { signal_ids: Array<number | string> },
    headers: { clientId: string; clientAccountId: string }
  ): Observable<unknown> {
    const additionalHeaders = new HttpHeaders({
      'X-CB-Client-Id': headers.clientId,
      'X-CB-Client-Account-Id': headers.clientAccountId,
    });
    return this.baseApiService.post<unknown>(this.manualRunUrl, payload, { showError: true, additionalHeaders });
  }

  getGrowthSignalsReportDetails(reportId: string): Observable<GrowthSignalsReportDetailsApiDto> {
    return this.baseApiService.get<GrowthSignalsReportDetailsApiDto>(`${this.reportDetailsUrl}/${reportId}/details`);
  }
}

